import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Typography, Grid, Box, Avatar, Button, Pagination, Skeleton, Tabs, Tab, Chip, Link, Tooltip, IconButton, Snackbar } from '@mui/material';
import FmdGoodOutlinedIcon from '@mui/icons-material/FmdGoodOutlined';
import LinkIcon from '@mui/icons-material/Link';
import { getUserProfile, updateUserProfile } from '../services/api';
import { UserProfile } from '../types/UserProfile';
import { AxiosResponse } from 'axios';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { SITE_NAME } from '../constants/site';
import OnlineStatus from '../components/OnlineStatus';
import GameListComponent from '../components/GameList/GameListComponent';
import CreateGameList from '../components/GameList/CreateGameList';
import { getUserGameLists, GameListSummary, GameList, deleteGameList } from '../services/contents_api';
import EditGameList from '../components/GameList/EditGameList';
import ShareButtons from '../components/common/ShareButtons';
import { isAuthenticated } from '../services/auth';
import ActivityTab from '../components/profile/Activity';
import ProfileTab from '../components/ProfileTab';
import SettingsIcon from '@mui/icons-material/Settings';
import GetAppIcon from '@mui/icons-material/GetApp';
import Expandable from '../components/common/Expandable';
import { isPwa } from '../utils/isPwa';
import ConstructionIcon from '@mui/icons-material/Construction';
import ReviewsTab from '../components/ReviewsTab';
import MemoriesTab from '../components/MemoriesTab';
import FollowButton from '../components/common/FollowButton';
import FollowsTab from '../components/FollowsTab';
import TimelineTab from '../components/TimelineTab';
import BoltIcon from '@mui/icons-material/Bolt';
import PlayLogsTab from '../components/PlayLogsTab';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import GotyTab from '../components/GotyTab';
import FeatureAnnouncementDialog from '../components/dialogs/FeatureAnnouncementDialog';
import SocialLinks from '../components/profile/SocialLinks';
import { getLatestPopupReleaseNote } from '../services/api';
import { ReleaseNote } from '../types/ReleaseNote';
import { getSteamPlayerSummary, SteamPlayerSummary, getGameIdBySteamId } from '../services/SteamApi';
import GameLibraryTab from '../components/library/GameLibraryTab';
import AnimeLibraryTab from '../components/library/AnimeLibraryTab';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import MovieIcon from '@mui/icons-material/Movie';

type MediaType = 'game' | 'anime' | 'movie';

const MEDIA_ITEMS: { value: MediaType; label: string; icon: React.ReactNode }[] = [
  { value: 'game', label: 'ゲーム', icon: <SportsEsportsIcon sx={{ fontSize: '0.9rem' }} /> },
  { value: 'anime', label: 'アニメ', icon: <LiveTvIcon sx={{ fontSize: '0.9rem' }} /> },
  { value: 'movie', label: '映画', icon: <MovieIcon sx={{ fontSize: '0.9rem' }} /> },
];

const MediaSegmentedControl: React.FC<{
  value: MediaType;
  onChange: (e: React.SyntheticEvent, v: MediaType) => void;
  items?: typeof MEDIA_ITEMS;
}> = ({ value, onChange, items = MEDIA_ITEMS }) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const itemRefs = React.useRef<(HTMLDivElement | null)[]>([]);
  const [indicator, setIndicator] = React.useState<{ left: number; width: number } | null>(null);
  const mounted = React.useRef(false);

  React.useLayoutEffect(() => {
    const activeIndex = items.findIndex(i => i.value === value);
    const el = itemRefs.current[activeIndex];
    if (el) {
      setIndicator({ left: el.offsetLeft, width: el.offsetWidth });
    }
    mounted.current = true;
  }, [value, items]);

  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'inline-flex',
        position: 'relative',
        backgroundColor: 'rgba(255, 255, 255, 0.03)',
        borderRadius: '8px',
        p: '3px',
        mb: 1.5,
        mt: -1,
        border: '1px solid rgba(255, 255, 255, 0.1)',
      }}
    >
      {indicator && (
        <Box
          sx={{
            position: 'absolute',
            top: '3px',
            left: `${indicator.left}px`,
            width: `${indicator.width}px`,
            height: 'calc(100% - 6px)',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '6px',
            transition: mounted.current
              ? 'left 0.2s cubic-bezier(0.4, 0, 0.2, 1), width 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
              : 'none',
            pointerEvents: 'none',
          }}
        />
      )}
      {items.map((item, i) => (
        <Box
          key={item.value}
          ref={(el: HTMLDivElement | null) => { itemRefs.current[i] = el; }}
          onClick={(e) => onChange(e, item.value)}
          sx={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: { xs: 1.2, sm: 2 },
            py: { xs: 0.25, sm: 0.25 },
            borderRadius: '6px',
            fontSize: { xs: '0.7rem', sm: '0.75rem' },
            fontWeight: value === item.value ? 500 : 400,
            cursor: 'pointer',
            transition: 'color 0.2s ease',
            color: value === item.value ? 'text.secondary' : 'rgba(255, 255, 255, 0.5)',
            userSelect: 'none',
            '&:hover': {
              color: value === item.value ? 'text.secondary' : 'rgba(255, 255, 255, 0.7)',
            },
          }}
        >
          {item.icon}
          {item.label}
        </Box>
      ))}
    </Box>
  );
};

interface ProfilePageProps {
  profile?: UserProfile;
  isPublic?: boolean;
  isOwnProfile?: boolean;
}

// タブの種類を定数で定義
const MAIN_TABS = {
  PROFILE: 'profile',
  LIBRARY: 'library',
  LISTS: 'lists',
  REVIEWS: 'reviews',
  MEMORIES: 'memories',
  ACTIVITY: 'activity',
  FOLLOWS: 'follows',
  TIMELINE: 'timeline',
  PLAYLOGS: 'playlogs',
  GOTY: 'goty',
} as const;

type MainTabType = typeof MAIN_TABS[keyof typeof MAIN_TABS];

const ProfilePage: React.FC<ProfilePageProps> = ({ 
  profile: initialProfile, 
  isPublic = false,
  isOwnProfile = false
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { username } = useParams<{ username: string }>();
  const queryParams = new URLSearchParams(location.search);
  
  // 初期値の設定を修正
  const tab = queryParams.get('tab') as MainTabType;
  const initialMedia = (queryParams.get('media') as MediaType) || 'game';

  const [mainTabValue, setMainTabValue] = useState<MainTabType>(
    tab || (isPublic ? MAIN_TABS.PROFILE : MAIN_TABS.LIBRARY)
  );
  const [mediaType, setMediaType] = useState<MediaType>(initialMedia);

  const [profile, setProfile] = useState<UserProfile | null>(initialProfile || null);
  const [isEditing, setIsEditing] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [gameCounts, setGameCounts] = useState<number[]>([0, 0, 0, 0, 0]);
  const [animeCounts, setAnimeCounts] = useState<number[]>([0, 0, 0, 0, 0]);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [createListDialogOpen, setCreateListDialogOpen] = useState(false);
  const [editingList, setEditingList] = useState<GameList | null>(null);
  const initialListPage = parseInt(queryParams.get('list_page') || '1', 10);
  const [listPage, setListPage] = useState(initialListPage);
  const [latestReleaseNote, setLatestReleaseNote] = useState<ReleaseNote | null>(null);
  const [steamStatus, setSteamStatus] = useState<SteamPlayerSummary | null>(null);

  const scrollPositionRef = useRef<number>(0);

  // スクロール位置を保存
  useEffect(() => {
    const handleScroll = () => {
      scrollPositionRef.current = window.scrollY;
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 認証チェックを追加
  useEffect(() => {
    const checkAuth = async () => {
      if (!isPublic) {
        const authenticated = await isAuthenticated();
        if (!authenticated) {
          navigate('/login', { state: { from: location.pathname } });
        }
      }
    };

    checkAuth();
  }, [isPublic, navigate, location.pathname]);

  // リリースノートを取得（非公開プロフィールのみ）
  useEffect(() => {
    const fetchLatestReleaseNote = async () => {
      if (!isPublic) {
        try {
          const data = await getLatestPopupReleaseNote();
          setLatestReleaseNote(data);
        } catch (error) {
          console.error('リリースノートの取得に失敗しました:', error);
        }
      }
    };

    fetchLatestReleaseNote();
  }, [isPublic]);

  const fetchProfile = useCallback(async () => {
    if (isPublic || initialProfile) return;
    try {
      const response: AxiosResponse<UserProfile> = await getUserProfile();
      setProfile(response.data);
      setGameCounts(response.data.status_counts || [0, 0, 0, 0, 0]);
      setAnimeCounts(response.data.anime_status_counts || [0, 0, 0, 0, 0]);
    } catch (error) {
      console.error('プロフィールの取得に失敗しました', error);
    } finally {
      setLoadingProfile(false);
    }
  }, [isPublic, initialProfile]);

  useEffect(() => {
    if (isPublic && initialProfile) {
      setProfile(initialProfile);
      setGameCounts(initialProfile.status_counts || [0, 0, 0, 0, 0]);
      setAnimeCounts(initialProfile.anime_status_counts || [0, 0, 0, 0, 0]);
      setLoadingProfile(false);
    } else if (!isPublic) {
      fetchProfile();
    }
  }, [isPublic, initialProfile, fetchProfile]);

  // Steamステータスを取得（steam_idがある場合）
  useEffect(() => {
    const fetchSteamStatus = async () => {
      if (profile?.steam_id) {
        const status = await getSteamPlayerSummary(profile.steam_id);
        setSteamStatus(status);
      }
    };
    fetchSteamStatus();
  }, [profile?.steam_id]);

  // Steamプレイ中ゲームのクリックハンドラー
  const handleSteamGameClick = async () => {
    if (!steamStatus?.gameid) return;

    // PULSEのgame_idを取得
    const pulseGameId = await getGameIdBySteamId(steamStatus.gameid);
    if (pulseGameId) {
      navigate(`/game/${pulseGameId}`);
    } else {
      setSnackbarMessage('データベースに登録されていないか、PULSEとSteamの連携が完了していないゲームです');
      setOpenSnackbar(true);
    }
  };

  const handleEditClick = () => {
    navigate('/profile/edit');
  };

  const handleSave = async (updatedProfile: UserProfile) => {
    try {
      const response = await updateUserProfile(updatedProfile);
      setIsEditing(false);
      setProfile(response.data);
    } catch (error: any) {
      if (error.response && error.response.data) {
        console.error('プロフィールの更新に失敗しました', error.response.data);
        throw error; // エラーを再スローして、ProfileEditFormで処理できるようにする
      }
    }
  };

  const handleCopyLink = () => {
    const publicProfileUrl = `${window.location.origin}/profile/${profile?.username}`;
    navigator.clipboard.writeText(publicProfileUrl);
    setSnackbarMessage('リンクがコピーされました');
    setOpenSnackbar(true);
  };

  // メインタブ変更ハンドラー
  const handleMainTabChange = (event: React.SyntheticEvent, newValue: MainTabType) => {
    const currentScroll = scrollPositionRef.current;
    setMainTabValue(newValue);

    const params = new URLSearchParams();
    params.set('tab', newValue);

    params.set('media', mediaType);
    if (newValue === MAIN_TABS.LISTS) {
      params.set('list_page', listPage.toString());
    }

    const newUrl = isPublic
      ? `/profile/${username}?${params.toString()}`
      : `/profile?${params.toString()}`;
    navigate(newUrl, { replace: true });

    requestAnimationFrame(() => {
      window.scrollTo(0, currentScroll);
    });

    const target = event.currentTarget as HTMLElement;
    if (target) {
      setTimeout(() => {
        target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }, 0);
    }
  };

  // メディアタイプ切替ハンドラー
  const handleMediaChange = (_event: React.SyntheticEvent, newMedia: MediaType) => {
    setMediaType(newMedia);

    // メディア切替時、対象メディアで利用不可のタブならライブラリにリセット
    const validAnimeTabs: MainTabType[] = [MAIN_TABS.LIBRARY, MAIN_TABS.REVIEWS];
    let newTab = mainTabValue;
    if (newMedia === 'anime' && !validAnimeTabs.includes(mainTabValue)) {
      newTab = MAIN_TABS.LIBRARY;
      setMainTabValue(MAIN_TABS.LIBRARY);
    } else if (newMedia === 'movie') {
      newTab = MAIN_TABS.LIBRARY;
      setMainTabValue(MAIN_TABS.LIBRARY);
    }

    const params = new URLSearchParams({
      tab: newTab,
      media: newMedia,
    });
    const newUrl = isPublic
      ? `/profile/${username}?${params.toString()}`
      : `/profile?${params.toString()}`;
    navigate(newUrl, { replace: true });
  };

  // React Query でゲームリストを取得
  const queryClient = useQueryClient();
  const stableListUsername = isPublic ? username : 'me';
  const { data: gameListsData } = useQuery({
    queryKey: ['gameLists', stableListUsername, listPage],
    queryFn: () => getUserGameLists(isPublic ? username : undefined, listPage),
    enabled: mainTabValue === MAIN_TABS.LISTS,
    staleTime: isPublic ? 30000 : 2000,
  });

  const gameLists: GameListSummary[] = gameListsData?.results || [];
  const listTotalPages = Math.ceil((gameListsData?.count || 0) / 9);

  // リストの編集ハンドラー
  const handleEditList = (list: GameListSummary) => {
    setEditingList(list as unknown as GameList); // 一時的な型キャスト
  };

  const invalidateGameLists = () => {
    queryClient.invalidateQueries({ queryKey: ['gameLists', stableListUsername] });
  };

  // リストの削除ハンドラー
  const handleDeleteList = async (listId: number) => {
    if (window.confirm('このリストを削除してもよろしいですか？')) {
      try {
        await deleteGameList(listId);
        invalidateGameLists();
      } catch (error) {
        console.error('リストの削除に失敗しました:', error);
      }
    }
  };

  // URLのパラメータが変更されたときの処理
  useEffect(() => {
    const newTab = queryParams.get('tab') as MainTabType;
    const newMedia = (queryParams.get('media') as MediaType) || 'game';
    const newListPage = parseInt(queryParams.get('list_page') || '1', 10);

    // メディアタイプに応じたタブのバリデーション
    const validAnimeTabs: MainTabType[] = [MAIN_TABS.LIBRARY, MAIN_TABS.REVIEWS];
    let effectiveTab = newTab || (isPublic ? MAIN_TABS.PROFILE : MAIN_TABS.LIBRARY);
    if (newMedia === 'anime' && !validAnimeTabs.includes(effectiveTab)) {
      effectiveTab = MAIN_TABS.LIBRARY;
    } else if (newMedia === 'movie') {
      effectiveTab = MAIN_TABS.LIBRARY;
    }

    if (effectiveTab !== mainTabValue) {
      setMainTabValue(effectiveTab);
    }

    if (newMedia !== mediaType) {
      setMediaType(newMedia);
    }

    if (newTab === MAIN_TABS.LISTS && newListPage !== listPage) {
      setListPage(newListPage);
    }
  }, [location.search]);

  // リストページ変更ハンドラーを修正
  const handleListPageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setListPage(value);
    // URLを更新
    const params = new URLSearchParams(queryParams);
    params.set('list_page', value.toString());
    params.set('tab', MAIN_TABS.LISTS);
    
    const newUrl = isPublic
      ? `/profile/${username}?${params.toString()}`
      : `/profile?${params.toString()}`;
    navigate(newUrl, { replace: true });
    
    // ページトップにスクロール
    window.scrollTo(0, 0);
  };

  return (
    <>
      <Helmet>
        <title>
          {isPublic && profile 
            ? `${profile.display_name || profile.username}(@${profile.username})のゲームライブラリ | ${SITE_NAME}`
            : `マイページ | ${SITE_NAME}`}
        </title>
      </Helmet>
      
      <Grid container spacing={3} sx={{ mb: { xs: 2, md: 2 } }}>
        <Grid item xs={12} md={8}>
          {loadingProfile ? (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Skeleton variant="circular" width={56} height={56} />
              <Box sx={{ ml: 2, flexGrow: 1 }}>
                <Skeleton width="40%" height={32} />
                <Skeleton width="60%" />
                <Skeleton width="80%" />
              </Box>
            </Box>
          ) : (
            profile && (
              <>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  mb: 1
                }}>
                  <Avatar
                    src={profile.avatar || undefined}
                    sx={{ width: 56, height: 56 }}
                  />
                  <Box sx={{ ml: 2 }}>
                    <Typography variant="h6" component="h1">{profile.display_name || profile.username}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body2" color="text.secondary">
                        @{profile.username}
                      </Typography>
                      {isPublic && profile.last_access && !profile.current_playing_game_id && !steamStatus?.gameextrainfo && (
                        <OnlineStatus lastAccess={profile.last_access} />
                      )}
                      {/* Steamでプレイ中のゲーム表示 */}
                      {steamStatus?.gameextrainfo && !profile.current_playing_game_id && (
                        <Box
                          onClick={handleSteamGameClick}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            backgroundColor: 'rgba(102, 192, 244, 0.12)',
                            border: '1px solid rgba(102, 192, 244, 0.5)',
                            borderRadius: '4px',
                            padding: '1px 8px',
                            paddingLeft: '4px',
                            textDecoration: 'none',
                            position: 'relative',
                            overflow: 'hidden',
                            cursor: steamStatus.gameid ? 'pointer' : 'default',
                            '&:hover': {
                              opacity: steamStatus.gameid ? 0.8 : 1
                            },
                            '&::after': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background: 'linear-gradient(90deg, rgba(102, 192, 244, 0), rgba(102, 192, 244, 0.2), rgba(102, 192, 244, 0))',
                              backgroundSize: '200% 100%',
                              animation: 'shimmer 2s infinite',
                            },
                            '@keyframes shimmer': {
                              '0%': {
                                backgroundPosition: '100% 0'
                              },
                              '100%': {
                                backgroundPosition: '-100% 0'
                              }
                            }
                          }}
                        >
                          <Box
                            sx={{
                              width: '0.85rem',
                              height: '0.85rem',
                              maskImage: 'url(/icons/service/steam_black.svg)',
                              maskSize: 'contain',
                              maskRepeat: 'no-repeat',
                              maskPosition: 'center',
                              WebkitMaskImage: 'url(/icons/service/steam_black.svg)',
                              WebkitMaskSize: 'contain',
                              WebkitMaskRepeat: 'no-repeat',
                              WebkitMaskPosition: 'center',
                              backgroundColor: '#e6e6e6',
                              zIndex: 1,
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#e6e6e6',
                              fontSize: '0.7rem',
                              fontWeight: 400,
                              lineHeight: '1',
                              letterSpacing: '0.02em',
                              maxWidth: '180px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              zIndex: 1,
                            }}
                          >
                            {steamStatus.gameextrainfo}
                          </Typography>
                        </Box>
                      )}
                      {profile.current_playing_game_id && (
                        <Box
                          onClick={() => navigate(`/game/${profile.current_playing_game_id}`)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            backgroundColor: 'rgba(217, 255, 0, 0.12)',
                            border: '1px solid rgba(217, 255, 0, 0.5)',
                            borderRadius: '4px',
                            padding: '1px 8px',
                            paddingLeft: '4px',
                            position: 'relative',
                            overflow: 'hidden',
                            cursor: 'pointer',
                            '&:hover': {
                              opacity: 0.8
                            },
                            '&::after': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background: 'linear-gradient(90deg, rgba(217, 255, 0, 0), rgba(217, 255, 0, 0.2), rgba(217, 255, 0, 0))',
                              backgroundSize: '200% 100%',
                              animation: 'shimmer 2s infinite',
                            },
                            '@keyframes shimmer': {
                              '0%': {
                                backgroundPosition: '100% 0'
                              },
                              '100%': {
                                backgroundPosition: '-100% 0'
                              }
                            }
                          }}
                        >
                          <PlayArrowIcon 
                            sx={{ 
                              fontSize: '0.875rem',
                              color: '#d9ff00',
                            }} 
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#d9ff00',
                              fontSize: '0.7rem',
                              fontWeight: 400,
                              lineHeight: '1',
                              letterSpacing: '0.02em',
                              zIndex: 1
                            }}
                          >
                            now playing
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
                {profile.bio && (
                  <Expandable maxLines={3} expandButtonStyle="icon">
                    <Typography variant="body2" sx={{ mb: 0.5, lineHeight: 1.25, whiteSpace: 'pre-wrap' }}>
                      {profile.bio}
                    </Typography>
                  </Expandable>
                )}
                {isPublic && profile.location && (
                  <Box sx={{
                    mb: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    color: 'text.secondary',
                  }}>
                    <FmdGoodOutlinedIcon sx={{ fontSize: '1rem' }} />
                    <Typography variant="body2" sx={{ fontSize: '0.75rem'}}>{profile.location}</Typography>
                  </Box>
                )}
                {profile.social_links && Object.keys(profile.social_links).length > 0 && (
                  <SocialLinks
                    socialLinks={profile.social_links}
                    onCopy={(message) => {
                      setSnackbarMessage(message);
                      setOpenSnackbar(true);
                    }}
                  />
                )}
                {!isPublic && (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
                      <Link
                        href={`/profile/${profile.username}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Chip
                          icon={<LinkIcon sx={{ fontSize: '0.875rem !important' }} />}
                          label={`公開URL: /profile/${profile.username}`}
                          size="small"
                          sx={{
                            height: '24px',
                            fontSize: '0.75rem',
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            color: 'text.secondary',
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.08)',
                              borderColor: 'primary.main',
                            },
                            '& .MuiChip-icon': {
                              marginLeft: '6px',
                              color: 'text.secondary'
                            }
                          }}
                        />
                      </Link>
                      <ShareButtons
                        text={`${profile.display_name || profile.username}のゲームライブラリ | ${SITE_NAME} - ゲーム記録管理プラットフォーム`}
                        url={`/profile/${profile.username}`}
                        hashtags={['PULSE_DB']}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<SettingsIcon />}
                        onClick={() => navigate('/profile/settings')}
                        sx={{
                          color: 'text.primary',
                          backgroundColor: 'rgba(255, 255, 255, 0.04)',
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: '50px',
                          '&:hover': {
                            backgroundColor: 'action.hover',
                            borderColor: 'primary.main',
                          }
                        }}
                      >
                        プロフィール設定
                      </Button>
                      {!isPwa() && (
                        <Tooltip title="PWAアプリをインストール">
                          <IconButton
                            size="small"
                            onClick={() => navigate('/install-pwa')}
                            sx={{ 
                              border: '1px solid',
                              borderColor: 'divider',
                              backgroundColor: 'rgba(255, 255, 255, 0.04)',
                              '&:hover': {
                                backgroundColor: 'action.hover',
                                borderColor: 'primary.main',
                              }
                            }}
                          >
                            <GetAppIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                )}
                {isPublic && (
                  <Box sx={{ mt: 1 }}>
                    <FollowButton
                      username={profile.username}
                      isFollowing={profile.is_following}
                      isOwnProfile={isOwnProfile}
                      isFollowedBy={profile.is_followed_by}
                      onFollowChange={(isFollowing) => {
                        setProfile(prev => prev ? {
                          ...prev,
                          is_following: isFollowing,
                          followers_count: prev.followers_count + (isFollowing ? 1 : -1)
                        } : null);
                      }}
                    />
                  </Box>
                )}
              </>
            )
          )}
        </Grid>
        <Grid item xs={12} md={4} sx={{ display: { xs: 'none', md: 'block' } }}>
          {/* 右側のカラムに追加のコンテンツを配置できます */}
        </Grid>
      </Grid>

      {/* メディアタイプ切替（アニメデータがある場合のみ表示） */}
      {animeCounts.some(c => c > 0) && (
        <MediaSegmentedControl value={mediaType} onChange={handleMediaChange} />
      )}

      {mediaType === 'movie' && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <MovieIcon sx={{ fontSize: 48, color: 'rgba(255, 255, 255, 0.2)', mb: 2 }} />
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            映画ライブラリは近日公開予定です
          </Typography>
        </Box>
      )}

      {/* メインタブ */}
      {mediaType !== 'movie' && (
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={mainTabValue}
          onChange={handleMainTabChange}
          variant="scrollable"
          sx={{
            minHeight: { xs: '32px', sm: '40px' },
            '& .MuiTab-root': {
              minHeight: { xs: '36px', sm: '40px' },
              py: { xs: 1, sm: 1 },
              px: { xs: 1, sm: 1.5 },
              fontSize: { xs: '0.65rem', sm: '0.75rem' },
              minWidth: 0
            }
          }}
        >
          {!isPublic && mediaType === 'game' && (
            <Tab
              icon={
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 24,
                    height: 24,
                    borderRadius: '4px',
                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.12)'
                    }
                  }}
                >
                  <BoltIcon
                    sx={{
                      fontSize: '1rem',
                      transform: 'rotate(20deg)'
                    }}
                  />
                </Box>
              }
              value={MAIN_TABS.TIMELINE}
            />
          )}
          {mediaType === 'game' && <Tab label="プロフィール" value={MAIN_TABS.PROFILE} />}
          <Tab label="ライブラリ" value={MAIN_TABS.LIBRARY} />
          <Tab label="レビュー" value={MAIN_TABS.REVIEWS} />
          {mediaType === 'game' && <Tab label="メモリー" value={MAIN_TABS.MEMORIES} />}
          {mediaType === 'game' && <Tab label="プレイログ" value={MAIN_TABS.PLAYLOGS} />}
          {mediaType === 'game' && <Tab label="リスト" value={MAIN_TABS.LISTS} />}
          {mediaType === 'game' && <Tab label="GOTY" value={MAIN_TABS.GOTY} />}
          {mediaType === 'game' && <Tab label="アクティビティ" value={MAIN_TABS.ACTIVITY} />}
          {mediaType === 'game' && <Tab label="フォロー" value={MAIN_TABS.FOLLOWS} />}
        </Tabs>
      </Box>
      )}

      {/* ライブラリタブのコンテンツ */}
      {mainTabValue === MAIN_TABS.LIBRARY && mediaType !== 'movie' && (
        <Box sx={{ width: '100%' }}>
          {mediaType === 'game' && (
            <GameLibraryTab
              profile={profile}
              isPublic={isPublic}
              gameCounts={gameCounts}
              mainTabValue={mainTabValue}
            />
          )}
          {mediaType === 'anime' && (
            <AnimeLibraryTab
              profile={profile}
              isPublic={isPublic}
              animeCounts={animeCounts}
              mainTabValue={mainTabValue}
            />
          )}
        </Box>
      )}

      {/* コンテンツタブのコンテンツ */}
      {mainTabValue === MAIN_TABS.LISTS && (
        <Box>
          {!isPublic && (
            <Button 
              variant="contained"
              size="small"
              onClick={() => setCreateListDialogOpen(true)}
              sx={{ 
                mb: 3,
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                backdropFilter: 'blur(8px)',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                color: 'rgba(255, 255, 255, 0.9)',
                fontWeight: 400,
                letterSpacing: '0.02em',
                textTransform: 'none',
                borderRadius: '6px',
                px: 2,
                py: 0.5,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.12)',
                  transform: 'translateY(-1px)'
                },
                '&:active': {
                  transform: 'translateY(0)'
                }
              }}
            >
              新規リストを作成
            </Button>
          )}
          
          <Grid container spacing={2}>
            {gameLists.map((list) => (
              <GameListComponent
                key={list.id}
                list={list}
                onEdit={!isPublic ? handleEditList : undefined}
                onDelete={!isPublic ? handleDeleteList : undefined}
              />
            ))}
          </Grid>

          {listTotalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={listTotalPages}
                page={listPage}
                onChange={handleListPageChange}
              />
            </Box>
          )}

          <CreateGameList
            open={createListDialogOpen}
            onClose={() => setCreateListDialogOpen(false)}
            onListCreated={invalidateGameLists}
          />

          {editingList && (
            <EditGameList
              open={!!editingList}
              onClose={() => setEditingList(null)}
              list={editingList}
              onListUpdated={invalidateGameLists}
            />
          )}
        </Box>
      )}

      {/* GOTYタブのコンテンツ */}
      {mainTabValue === MAIN_TABS.GOTY && profile && (
        <GotyTab
          username={profile.username}
          isPublic={isPublic}
        />
      )}

      {/* レビュータブのコンテンツ */}
      {mainTabValue === MAIN_TABS.REVIEWS && (
        mediaType === 'anime' ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <ConstructionIcon sx={{ fontSize: 48, color: 'rgba(255, 255, 255, 0.2)', mb: 2 }} />
            <Typography variant="h6" sx={{ fontSize: '0.95rem', fontWeight: 600, mb: 1 }}>
              準備中
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              レビュータブは現在準備中です
            </Typography>
          </Box>
        ) : (
          <ReviewsTab
            username={profile?.username}
            displayName={profile?.display_name}
            isPublic={isPublic}
          />
        )
      )}

      {/* メモリータブのコンテンツ */}
      {mainTabValue === MAIN_TABS.MEMORIES && (
        <MemoriesTab
          username={profile?.username}
          isPublic={isPublic}
          userProfile={profile}
        />
      )}

      {/* アクティビティタブのコンテンツ */}
      {mainTabValue === MAIN_TABS.ACTIVITY && (
        <ActivityTab 
          username={profile?.username} 
          isPublic={isPublic} 
        />
      )}

      {/* プロフィールタブのコンテンツ */}
      {mainTabValue === MAIN_TABS.PROFILE && (
        <ProfileTab 
          profile={profile!}
          isPublic={isPublic}
        />
      )}

      {/* フォロータブのコンテンツ */}
      {mainTabValue === MAIN_TABS.FOLLOWS && (
        <FollowsTab
          username={profile?.username}
          isPublic={isPublic}
        />
      )}

      {/* タイムラインタブのコンテンツ */}
      {mainTabValue === MAIN_TABS.TIMELINE && (
        <TimelineTab 
          username={profile?.username || ''} 
          isPublic={isPublic} 
        />
      )}

      {/* プレイログタブのコンテンツ */}
      {mainTabValue === MAIN_TABS.PLAYLOGS && profile && (
        <PlayLogsTab
          username={profile.username}
          isPublic={isPublic}
        />
      )}

      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        message={snackbarMessage}
      />

      {/* リリースノートのお知らせダイアログ */}
      {!isPublic && latestReleaseNote && (
        <FeatureAnnouncementDialog
          announcement={{
            id: `release-note-${latestReleaseNote.id}`,
            title: latestReleaseNote.title,
            description: latestReleaseNote.description,
            linkText: latestReleaseNote.link_text,
            linkUrl: latestReleaseNote.link_url,
            badge: 'NEW',
            badgeColor: 'primary',
            version: latestReleaseNote.version,
          }}
          storageKey="latest-release-note-dismissed"
          onNavigate={latestReleaseNote.link_url ? () => {
            if (latestReleaseNote.link_url!.startsWith('http')) {
              window.open(latestReleaseNote.link_url, '_blank', 'noopener noreferrer');
            } else {
              navigate(latestReleaseNote.link_url!);
            }
          } : undefined}
        />
      )}
    </>
  );
};

export default ProfilePage;
